import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ZeosApiContext } from "../context/ZeosApiContext";
import {
  keepLastGoodByRequestedId,
  resourceMatchesRequestedId,
} from "../services/helpers/DashboardHelpers";
import { getSearchParamNumber } from "../services/nav/QueryStringHelpers";
import { ICompanyContract } from "../services/zeos-api/contracts/Company";
import { IPropertyContract } from "../services/zeos-api/contracts/Property";

const isSuccessPayload = (
  zeosStatus?: string | null,
  data?: unknown
): boolean => zeosStatus?.toLowerCase() === "success" && data != null;

const payloadMatchesRequestedId = (
  payloadId: number | undefined,
  requestedId: number
): boolean =>
  payloadId == null || resourceMatchesRequestedId(payloadId, requestedId);

export const useDataFromParams = () => {
  const api = useContext(ZeosApiContext);
  const [params] = useSearchParams();
  const requestedPropertyId = getSearchParamNumber("property", params);
  const requestedCompanyId = getSearchParamNumber("company", params);

  const [property, setProperty] = useState<IPropertyContract>();
  const [company, setCompany] = useState<ICompanyContract>();
  const [settledPropertyId, setSettledPropertyId] = useState<number>();
  const [settledCompanyId, setSettledCompanyId] = useState<number>();
  // The property id the API last refused with 403, so screens can say so instead of
  // quietly rendering an empty dashboard.
  const [deniedPropertyId, setDeniedPropertyId] = useState<number>();

  useEffect(() => {
    let cancelled = false;
    const loadProperty = async () => {
      if (requestedPropertyId == null) {
        setProperty(undefined);
        setSettledPropertyId(undefined);
        return;
      }

      const prop = await api.Property.getProperty(requestedPropertyId);
      if (cancelled) {
        return;
      }

      const matched =
        isSuccessPayload(prop.zeosStatus, prop.data) &&
        payloadMatchesRequestedId(prop.data?.propertyId, requestedPropertyId);
      setProperty((prev) =>
        matched
          ? prop.data
          : keepLastGoodByRequestedId(
              prev,
              prev?.propertyId,
              requestedPropertyId
            )
      );
      setDeniedPropertyId(
        prop.httpStatus === 403 ? requestedPropertyId : undefined
      );
      setSettledPropertyId(requestedPropertyId);
    };
    loadProperty();
    return () => {
      cancelled = true;
    };
  }, [requestedPropertyId, api.Property]);

  useEffect(() => {
    let cancelled = false;
    const loadCompany = async () => {
      if (requestedCompanyId == null) {
        setCompany(undefined);
        setSettledCompanyId(undefined);
        return;
      }

      const comp = await api.Company.getCompanyAsync(requestedCompanyId);
      if (cancelled) {
        return;
      }

      const matched =
        isSuccessPayload(comp.zeosStatus, comp.data) &&
        payloadMatchesRequestedId(comp.data?.companyId, requestedCompanyId);
      setCompany((prev) =>
        matched
          ? comp.data
          : keepLastGoodByRequestedId(
              prev,
              prev?.companyId,
              requestedCompanyId
            )
      );
      setSettledCompanyId(requestedCompanyId);
    };
    loadCompany();
    return () => {
      cancelled = true;
    };
  }, [requestedCompanyId, api.Company]);

  const keyedProperty =
    requestedPropertyId != null &&
    (property?.propertyId == null
      ? settledPropertyId === requestedPropertyId
      : resourceMatchesRequestedId(property.propertyId, requestedPropertyId))
      ? property
      : undefined;
  const keyedCompany =
    requestedCompanyId != null &&
    (company?.companyId == null
      ? settledCompanyId === requestedCompanyId
      : resourceMatchesRequestedId(company.companyId, requestedCompanyId))
      ? company
      : undefined;

  return {
    property: keyedProperty,
    company: keyedCompany,
    requestedPropertyId,
    requestedCompanyId,
    propertyPending:
      requestedPropertyId != null && settledPropertyId !== requestedPropertyId,
    propertyAccessDenied:
      requestedPropertyId != null && deniedPropertyId === requestedPropertyId,
  };
};
